import { Pagination, Select, Spin, Tooltip } from "antd";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiArrowRight, FiCopy, FiExternalLink, FiInfo, FiMapPin, FiPackage, FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import { MdRefresh } from "react-icons/md";
import { Link } from "react-router-dom";
import { Col, Input, InputGroup, InputGroupText, Row } from "reactstrap";
import { getAllCustomers, getShipmentTrackingAPI, resolveTrackingManuallyAPI } from "../../api/pagination";
import { refreshTracking, triggerUpdateAllTrackings, updateTrackingStatusApi } from "../../api/TrackingApi";
import NotFoundDataUI from "../../components/cards/NotFoundDataUI";
import PageName from "../../components/react_helmet/PageName";
import TableSkeleton from "../../components/skeleton/TableSkeleton";

const STAGE_PROGRESS = {
  "label created": { pct: 8, color: "#f59e0b" },
  "pending": { pct: 0, color: "#94a3b8" },
  "picked up": { pct: 25, color: "#6366f1" },
  "in transit": { pct: 55, color: "#3b82f6" },
  "out for delivery": { pct: 85, color: "#22c55e" },
  "exception": { pct: 50, color: "#ef4444" },
  "delivered": { pct: 100, color: "#10b981" },
};

const resolveStage = (desc, code) => {
  const d = (desc || "").toLowerCase();
  const c = (code || "").toUpperCase();
  if (c === "DL" || d.includes("delivered") || d.includes("completed")) return "delivered";
  if (c === "OD" || d.includes("out for delivery") || d.includes("on fedex vehicle") || d.includes("out for delivery")) return "out for delivery";
  if (["IT", "IX", "DP", "AR", "AE"].includes(c) || d.includes("in transit") || d.includes("departed") || d.includes("on the way") || d.includes("arrived") || d.includes("moving")) return "in transit";
  if (c === "PU" || d.includes("picked up")) return "picked up";
  if (c === "OC" || c === "IN" || d.includes("label") || d.includes("shipment information") || d.includes("info received") || d.includes("pre-transit")) return "label created";
  if (c === "DE" || c === "SE" || d.includes("exception") || d.includes("delay") || d.includes("stuck")) return "exception";
  return "pending";
};

const RouteProgress = ({ status, statusCode, originLabel, destLabel }) => {
  const normalizedStage = resolveStage(status, statusCode);
  const prog = STAGE_PROGRESS[normalizedStage] || STAGE_PROGRESS["pending"];
  const isComplete = normalizedStage === "delivered";
  return (
    <div style={{ marginTop: "6px", width: "100%" }}>
      {/* Labels */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
        <span style={{ fontSize: "10px", color: "#94a3b8", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "45%" }}>
          {originLabel || "Source"}
        </span>
        <span style={{ fontSize: "10px", color: isComplete ? "#059669" : "#64748b", fontWeight: isComplete ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "45%", textAlign: "right" }}>
          {destLabel || "Destination"}
        </span>
      </div>
      {/* Bar track */}
      <div style={{ position: "relative", height: "6px", borderRadius: "10px", background: "#f1f5f9", overflow: "hidden" }}>
        {/* Fill */}
        <div style={{
          height: "100%",
          width: `${prog.pct}%`,
          borderRadius: "10px",
          background: isComplete
            ? `linear-gradient(90deg, #34d399, #10b981)`
            : `linear-gradient(90deg, ${prog.color}cc, ${prog.color})`,
          transition: "width 0.4s ease",
        }} />
        {/* Truck dot */}
        {!isComplete && prog.pct > 0 && (
          <div style={{
            position: "absolute",
            top: "-2px",
            left: `calc(${prog.pct}% - 5px)`,
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: prog.color,
            border: "2px solid white",
            boxShadow: `0 0 0 2px ${prog.color}44`,
          }} />
        )}
      </div>
      {/* % label */}
      <div style={{ textAlign: "right", fontSize: "9px", color: prog.color, fontWeight: 700, marginTop: "2px" }}>
        {isComplete ? "✓ Delivered" : `${prog.pct}%`}
      </div>
    </div>
  );
};

const getTrackingUrl = (service = "", trackNum = "") => {
  return `https://www.google.com/search?q=${encodeURIComponent(service + " tracking " + trackNum)}`;
};

const ShippingTracking = () => {
  const [data, setData] = useState([]);
  console.log('data', data)
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem("trackingActiveTab") || "all");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 0, currentPage: 1, pageSize: 50 });
  const [customers, setCustomers] = useState([]);
  const [updatingAll, setUpdatingAll] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [fetchingCustomers, setFetchingCustomers] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [carrier, setCarrier] = useState("");
  const [statusQuery, setStatusQuery] = useState("");

  const [pendingStatuses, setPendingStatuses] = useState({});
  const [updatingId, setUpdatingId] = useState(null);

  const handleUpdateStatus = (trackingId) => {
    const status = pendingStatuses[trackingId];
    if (!status) {
      toast.error("Please select a status");
      return;
    }

    setUpdatingId(trackingId);
    updateTrackingStatusApi({ trackingId, status }, (res) => {
      setUpdatingId(null);
      if (res.status === 200) {
        toast.success(res.data?.message || "Tracking status updated");
        fetchTracking();
      } else {
        toast.error(res.message || "Failed to update tracking status");
      }
    });
  };

  const [highlightedId, setHighlightedId] = useState(() => {
    const id = sessionStorage.getItem('highlightedTrackingId');
    if (id) {
      // Clear it so it only highlights once upon returning
      sessionStorage.removeItem('highlightedTrackingId');
    }
    return id;
  });

  const hasActiveFilters = searchTerm !== "" || activeTab !== "all" || carrier !== "" || statusQuery !== "" || selectedCustomer !== "" || sortOrder !== "desc";

  const clearAllFilters = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setActiveTab("all");
    setCarrier("");
    setStatusQuery("");
    setSelectedCustomer("");
    setSortOrder("desc");
    setPage(1);
  };

  const fetchTracking = () => {
    setLoading(true);
    getShipmentTrackingAPI(page, pageSize, selectedCustomer, activeTab, debouncedSearchTerm, sortOrder, carrier, statusQuery, (res) => {
      if (res.status === 200) {
        setData(res.data?.data || []);
        setPagination(res.data?.pagination || { totalItems: 0, totalPages: 0, currentPage: 1, pageSize: 50 });
      } else {
        setData([]);
      }
      setLoading(false);
    });
  };

  const handleCustomerSearch = (value) => {
    setFetchingCustomers(true);
    getAllCustomers(1, value, 50, "", (res) => {
      if (res && res.data) {
        setCustomers(res.data.data || []);
      }
      setFetchingCustomers(false);
    });
  };

  const debouncedCustomerSearch = useCallback(debounce((val) => handleCustomerSearch(val), 500), []);

  useEffect(() => {
    handleCustomerSearch("");
  }, []);

  const handleResolve = (trackingId) => {
    setResolvingId(trackingId);
    resolveTrackingManuallyAPI(trackingId, (res) => {
      setResolvingId(null);
      if (res.status === 200) {
        toast.success(res.data?.message || "Tracking manually resolved");
        fetchTracking();
      } else {
        toast.error(res.message || "Failed to resolve tracking");
      }
    });
  };

  const refreshTrackingData = (id) => {
    if (!id) return;
    setRefreshingId(id);
    refreshTracking(id, (res) => {
      setRefreshingId(null);
      if (res?.status === 200) {
        toast.success(res?.data || "Tracking data refreshed");
        fetchTracking();
      } else {
        toast.error("Failed to refresh tracking data");
      }
    });
  };

  const handleUpdateAllTrackings = () => {
    setUpdatingAll(true);
    triggerUpdateAllTrackings((res) => {
      setUpdatingAll(false);
      if (res?.status === 200) {
        toast.success(res?.data?.message || "Successfully triggered update for all trackings");
        fetchTracking();
      } else {
        toast.error(res?.message || "Failed to update trackings");
      }
    });
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem("trackingActiveTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    fetchTracking();
  }, [debouncedSearchTerm, activeTab, page, selectedCustomer, sortOrder, carrier, statusQuery, pageSize]);

  const cell = (flexBasis, extra = {}) => ({
    flex: `0 1 ${flexBasis}`,
    minWidth: 0,
    padding: "0 8px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    overflow: "hidden",
    ...extra,
  });

  const label = (txt) => (
    <span style={{ display: "block", fontSize: "10px", fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
      {txt}
    </span>
  );

  return (
    <>
      <PageName title="Tracking" />
      <style>{`
        @keyframes spin {
          100% { transform: rotate(360deg); }
        }
        .spin-animation {
          animation: spin 1s linear infinite;
        }
        .highlight-row {
          transform: scale(1.008);
          box-shadow: 0 6px 15px rgba(59, 130, 246, 0.25) !important;
          z-index: 10;
          position: relative;
          transition: all 0.3s ease;
        }
      `}</style>
      <div className="container-fluid py-2" style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
        <Row className="mb-4 align-items-center">
          <Col lg={5} md={12}>
            <h3 className="fw-bold mb-1">Global Shipping Tracking</h3>
            <p className="text-muted small mb-0">View all active and historical shipment trackings across all orders.</p>
          </Col>
          <Col lg={7} md={12} className="d-flex justify-content-end align-items-center gap-2 mt-3 mt-lg-0 flex-wrap flex-md-nowrap">
            <button
              className="btn btn-primary d-flex align-items-center justify-content-center"
              style={{ padding: "0 12px", height: "32px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}
              title="Trigger Update For All Trackings"
              onClick={handleUpdateAllTrackings}
              disabled={updatingAll}
            >
              {updatingAll ? <Spin size="small" style={{ marginRight: '8px' }} /> : <MdRefresh size={14} style={{ marginRight: '6px' }} />}
              Update All
            </button>
            <button
              className="btn btn-light border d-flex align-items-center justify-content-center"
              style={{ padding: "0 10px", height: "32px", borderRadius: "6px" }}
              title="Reload Tracking Data"
              onClick={() => {
                setPage(1);
                fetchTracking();
              }}
            >
              <FiRefreshCw size={14} color="#475569" className={loading ? "spin-animation" : ""} />
            </button>
            {hasActiveFilters && (
              <button
                className="btn d-flex align-items-center gap-1"
                style={{
                  padding: "0 12px",
                  height: "32px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#dc2626",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  transition: "all 0.2s",
                  whiteSpace: "nowrap",
                }}
                title="Clear All Filters"
                onClick={clearAllFilters}
              >
                <FiX size={13} />
                Clear Filters
              </button>
            )}
            <InputGroup style={{ maxWidth: "300px", borderRadius: "8px", overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
              <InputGroupText className="bg-white border-end-0 text-muted">
                <FiSearch />
              </InputGroupText>
              <Input
                placeholder="Search orders, PO, tracking..."
                className="border-start-0 ps-0"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ boxShadow: "none" }}
              />
            </InputGroup>
          </Col>
        </Row>

        <div className="d-flex mb-3 gap-2 align-items-center flex-wrap">
          <div className="d-flex gap-2">
            {["all", "stuck", "Coming To Techmicro", "Direct Ship"].map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); setPage(1); }}
                className={`btn px-3 py-1 text-capitalize ${activeTab === tab ? "shadow-sm border-0" : "btn-light text-muted border"}`}
                style={{
                  borderRadius: "8px",
                  transition: "all 0.2s",
                  background: activeTab === tab ? "#00509d" : "",
                  color: activeTab === tab ? "#ffffff" : ""
                }}
              >
                {tab === "all" ? "All Shipments" : tab === "stuck" ? "Issues" : tab}
              </button>
            ))}
          </div>

          <div className="ms-auto d-flex gap-2 align-items-center">
            <Select
              placeholder="Sort By Date"
              value={sortOrder}
              style={{ width: "150px" }}
              options={[
                { value: "desc", label: "Newest First" },
                { value: "asc", label: "Oldest First" }
              ]}
              onChange={(val) => { setSortOrder(val); setPage(1); }}
            />
            <Select
              placeholder="Filter by Carrier"
              value={carrier || undefined}
              style={{ width: "150px" }}
              allowClear
              options={[
                { value: "fedex", label: "FedEx" },
                { value: "ups", label: "UPS" },
                { value: "usps", label: "USPS" },
                { value: "others", label: "Others" }
              ]}
              onChange={(val) => { setCarrier(val || ""); setPage(1); }}
            />
            <Select
              placeholder="Filter by Status"
              value={statusQuery || undefined}
              style={{ width: "150px" }}
              allowClear
              options={[
                { value: "all", label: "All" },
                { value: "in transit", label: "In Transit" },
                { value: "delivered", label: "Delivered" },
                { value: "stuck", label: "Stuck" },
                { value: "pending", label: "Pending" },
                { value: "label created", label: "Label Created" },
                { value: "completed", label: "Completed" }
              ]}
              onChange={(val) => { setStatusQuery(val || ""); setPage(1); }}
            />
            <Select
              showSearch
              placeholder="Filter by Customer"
              loading={fetchingCustomers}
              onSearch={(value) => debouncedCustomerSearch(value)}
              onChange={(val) => { setSelectedCustomer(val || ""); setPage(1); }}
              filterOption={false}
              value={selectedCustomer || undefined}
              allowClear
              style={{ width: "250px" }}
            >
              {customers.map((c) => (
                <Select.Option key={c.id || c._id} value={c.quickbooks_id || c.id || c._id}>
                  {c.quickbooks_id ? `${c.quickbooks_id} - ` : ""}{c?.billing?.[0]?.company || "-"}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        <div className="tracking-list-container bg-white p-4 rounded shadow-sm border">
          {loading ? (
            <TableSkeleton columns={6} hideHeader />
          ) : data.map((item) => {
            const isDirectShip = item.trackingType === "Direct Ship";
            const accentColor = isDirectShip ? "#22c55e" : "#f97316";
            const trackUrl = item.trackingUrl || getTrackingUrl(item.carrier, item.trackingNumber);
            const isHighlighted = highlightedId === (item.trackingId || item._id);

            return (
              <div
                key={item.trackingId || item._id || Math.random()}
                className={isHighlighted ? "highlight-row" : ""}
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  width: "100%",
                  marginBottom: "8px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: `1px solid ${isHighlighted ? "#3b82f6" : (isDirectShip ? "#86efac" : "#fdba74")}`,
                  background: isHighlighted ? "#eff6ff" : (isDirectShip ? "#f0fdf4" : "#fff7ed"),
                  boxShadow: `0 4px 6px ${isDirectShip ? "rgba(34,197,94,0.15)" : "rgba(249,115,22,0.15)"}`,
                  minHeight: "70px",
                  boxSizing: "border-box",
                }}>

                {/* Accent bar */}
                <div style={{ width: "4px", background: accentColor, flexShrink: 0 }} />

                {/* ── ORDER / ITEM INFO ── */}
                <div style={cell("16%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
                  {label("Order Reference")}
                  <Link
                    to={{ pathname: `/admin/sale-order-details/${item.orderId}`, state: { fromTracking: true } }}
                    style={{ textDecoration: "none", marginBottom: "4px" }}
                    onClick={() => sessionStorage.setItem('highlightedTrackingId', item.trackingId || item._id)}
                  >
                    <span style={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#0369a1",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "2px 8px",
                      background: "#f0f9ff",
                      border: "1px solid #bae6fd",
                      borderRadius: "4px"
                    }}>
                      {item?.orderSequentialId} <FiExternalLink size={11} />
                    </span>
                  </Link>
                  <span style={{ fontSize: "11px", fontWeight: 600, color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.lineItems && item.lineItems.length > 0 ? item.lineItems.map(i => i.sku).join(", ") : "No Items"}
                  </span>
                </div>

                {/* ── CARRIER + TRACKING # ── */}
                <div style={cell("16%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "3px" }}>
                    <span style={{
                      background: "#7c3aed", color: "white",
                      fontSize: "9px", fontWeight: 800,
                      padding: "1px 6px", borderRadius: "3px", letterSpacing: "0.5px",
                    }}>
                      {item.carrier ? item.carrier.toUpperCase().slice(0, 8) : "N/A"}
                    </span>
                    <span style={{ fontSize: "10px", color: "#94a3b8", fontWeight: 500 }}>
                      3rd Party Carrier
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <a
                      href={trackUrl}
                      target="_blank" rel="noreferrer"
                      style={{ fontSize: "13px", fontWeight: 700, color: "#0369a1", textDecoration: "none", fontFamily: "monospace", letterSpacing: "0.3px", lineHeight: 1.2, display: "flex", alignItems: "center", gap: "4px" }}
                    >
                      {item.trackingNumber}
                      <FiExternalLink size={11} color="#94a3b8" />
                    </a>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigator.clipboard.writeText(item.trackingNumber);
                        toast.success("Copied to clipboard");
                      }}
                      style={{ background: "none", border: "none", padding: "2px", cursor: "pointer", display: "flex", alignItems: "center" }}
                      title="Copy Tracking Number"
                    >
                      <FiCopy size={13} color="#64748b" />
                    </button>
                  </div>
                </div>

                {/* ── ROUTE + PROGRESS ── */}
                <div style={cell("20%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
                  {label("Route")}
                  <div style={{ width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", flexWrap: "nowrap", overflow: "hidden" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: "2px", color: "#64748b", fontWeight: 500, fontSize: "11px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "42%" }}>
                        <FiMapPin size={9} color="#94a3b8" style={{ flexShrink: 0 }} />
                        {item.route?.origin?.city ? `${item.route.origin.city}, ${item.route.origin.state || ''}` : "Source"}
                      </span>
                      <FiArrowRight size={9} color="#cbd5e1" style={{ flexShrink: 0 }} />
                      <span style={{ display: "flex", alignItems: "center", gap: "2px", fontWeight: 700, color: "#334155", fontSize: "11px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "42%" }}>
                        <FiMapPin size={9} color={item.status === "Delivered" ? "#10b981" : "#f59e0b"} style={{ flexShrink: 0 }} />
                        {item.route?.destination?.city ? `${item.route.destination.city}, ${item.route.destination.state || ''}` : "Destination"}
                      </span>
                    </div>
                    <RouteProgress
                      status={item.status}
                      statusCode={item.statusCode}
                      originLabel={item.route?.origin?.city ? `${item.route.origin.city}, ${item.route.origin.state || ''}` : "Source"}
                      destLabel={item.route?.destination?.city ? `${item.route.destination.city}, ${item.route.destination.state || ''}` : "Destination"}
                    />
                  </div>
                </div>

                {/* ── STATUS ── */}
                <div style={cell("16%", { padding: "8px 10px", borderRight: "1px dashed #94a3b8" })}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 700,
                    backgroundColor: "#f8fafc", color: "#475569",
                    border: "1px solid #cbd5e1",
                    width: "fit-content",
                  }}>
                    <span style={{
                      width: "7px", height: "7px", borderRadius: "50%",
                      backgroundColor: item.status === "Delivered" ? "#22c55e" : "#eab308", display: "inline-block",
                    }} />
                    {item.status}
                  </span>

                  {(item.stuckReason && item.status?.toLowerCase().includes("stuck")) && (
                    <div style={{ marginTop: "4px" }}>
                      <Tooltip title={item.stuckReason || item.reason || item.exceptionReason || item.message || "No reason provided"}>
                        <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: 600, display: "flex", alignItems: "center", gap: "3px", cursor: "pointer", width: "fit-content" }}>
                          <FiInfo size={12} />
                          View Reason
                        </span>
                      </Tooltip>
                    </div>
                  )}

                  <span style={{ fontSize: "11px", color: "#94a3b8", marginTop: "3px", display: "flex", alignItems: "center", gap: "3px" }}>
                    <FiExternalLink size={9} />
                    Track on {item.carrier ? item.carrier : "carrier"}'s website
                  </span>
                </div>

                {/* ── DATE ── */}
                <div style={cell("10%", { padding: "8px 8px", borderRight: "1px dashed #94a3b8" })}>
                  {label("Created")}
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
                    {item.createdAt ? moment(item.createdAt).format("MMM DD, YYYY") : "—"}
                  </span>
                </div>

                {/* ── PACKAGE ── */}
                <div style={cell("8%", { padding: "8px 8px", borderRight: "1px dashed #94a3b8" })}>
                  {label("Package")}
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    {item.package?.weight?.value && (
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
                        ⚖ {item.package.weight.value} <span style={{ fontSize: "11px", fontWeight: 400, color: "#94a3b8" }}>{item.package.weight.unit || "LBS"}</span>
                      </span>
                    )}
                    <span style={{ fontSize: "13px", color: "#64748b", display: "flex", alignItems: "center", gap: "3px" }}>
                      <FiPackage size={11} color="#94a3b8" />
                      Qty: <strong style={{ color: "#334155" }}>{item.package?.qty || 0}</strong>
                    </span>
                  </div>
                </div>

                {/* ── TYPE & ACTIONS ── */}
                <div style={cell("14%", { padding: "8px 8px", display: "flex", flexDirection: "column", gap: "8px" })}>
                  <div>
                    {label("Type")}
                    <span style={{
                      padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700,
                      backgroundColor: isDirectShip ? "#f0fdf4" : "#fff7ed",
                      color: isDirectShip ? "#166534" : "#9a3412",
                      border: `1px solid ${isDirectShip ? "#bbf7d0" : "#fed7aa"}`,
                      width: "fit-content",
                    }}>{item.status?.toLowerCase() === "stuck" ? "Issues" : (item.status || "N/A")}</span>
                  </div>
                  {item.status && item.status.toLowerCase() === "stuck" && (
                    <div>
                      <button
                        onClick={() => handleResolve(item.trackingId)}
                        disabled={resolvingId === item.trackingId}
                        className="btn btn-sm shadow-sm"
                        style={{
                          fontSize: "11px", padding: "2px 8px",
                          backgroundColor: "#90f94fff", color: "#0f172a", border: "1px solid #cbd5e1"
                        }}
                      >
                        {resolvingId === item.trackingId ? <Spin size="small" /> : "Resolve Manually"}
                      </button>
                    </div>
                  )}
                  {item.status && item.status.toLowerCase() === "pending" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <select
                        className="form-select form-select-sm"
                        style={{ fontSize: "11px", padding: "2px 20px 2px 8px", minHeight: "24px" }}
                        value={pendingStatuses[item.trackingId || item._id] || ""}
                        onChange={(e) => setPendingStatuses({ ...pendingStatuses, [item.trackingId || item._id]: e.target.value })}
                      >
                        <option value="">Select Status</option>
                        <option value="Pending">Pending</option>
                        <option value="Label created">Label created</option>
                        <option value="In Transit">In Transit</option>
                        <option value="Out for Delivery">Out for Delivery</option>
                        <option value="completed">completed</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Stuck">Stuck</option>
                      </select>
                      <button
                        onClick={() => handleUpdateStatus(item.trackingId || item._id)}
                        disabled={updatingId === (item.trackingId || item._id) || !pendingStatuses[item.trackingId || item._id]}
                        className="btn btn-sm shadow-sm"
                        style={{
                          fontSize: "11px", padding: "2px 8px",
                          backgroundColor: "#3b82f6", color: "#ffffff", border: "1px solid #2563eb"
                        }}
                      >
                        {updatingId === (item.trackingId || item._id) ? <Spin size="small" /> : "Apply"}
                      </button>
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "auto" }}>
                    {refreshingId === (item.trackingId || item._id) ? (
                      <div style={{ padding: "3px", display: "flex", alignItems: "center" }}>
                        <Spin size="small" />
                      </div>
                    ) : (
                      <Tooltip title="Reload to see the latest status of tracking">
                        <button onClick={() => refreshTrackingData(item.trackingId || item._id)}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: "3px", display: "flex", alignItems: "center" }}>
                          <MdRefresh color="#0ea5e9" size={21} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>

              </div>
            );
          })}

          {!loading && data.length === 0 && (
            <div className="text-center py-4">
              <NotFoundDataUI
                message="No tracking information found!"
                onClearSearch={hasActiveFilters ? clearAllFilters : undefined}
              />
            </div>
          )}

          {!loading && data.length > 0 && pagination?.totalPages > 1 && (
            <div className="d-flex justify-content-end mt-4">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={pagination.totalItems}
                onChange={(p) => setPage(p)}
                showSizeChanger={true}
                onShowSizeChange={(current, size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                pageSizeOptions={['10', '25', '50', '100']}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ShippingTracking;
